import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Dialog from '@material-ui/core/Dialog';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import debounce from 'lodash.debounce';
import { getAllUsers, getCohortInfoForCoach } from '../../services/apiService';
import { getFullName } from '../../utils';
import { Backdrop, CircularProgress } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { coachesSelector } from '../../redux/reducers/coachReducer';
import {
  createCoachAction,
  deleteCoachAction,
  loadAllCoaches, toggleStatusAction,
  updateCoachAction, updatePinAction
} from '../../redux/actions/coachActions';
import { makeStyles } from '@material-ui/core/styles';
import 'ag-grid-enterprise';
import CoachTable from './table';
import {
  COURSE_TYPE,
  COURSE_TYPE_NAMES_MAPPING,
  DEBOUNCE_DELAY_MS,
} from '../../constants';
import { PageHeader } from '../../extras/PageLayout';
import useUrlFilters from '../../extras/useUrlFilters';

const COACH_ROLES = {
  mentor: 'Mentor',
  career_coach: 'Career Coach',
  instructor: 'Instructor',
  subject_matter_expert: 'Subject Matter Expert',
  evaluator: 'Evaluator',
  moderator: 'Moderator',
  alumni: 'Alumni',
  project_evaluator: 'Project Evaluator',
  business_communication_coach: 'Business Communication Coach',
};

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: 1500,
    color: '#fff',
  },
  dialogPaper: {
    borderRadius: '16px !important',
    backgroundColor: 'var(--card-background, #fff)',
    maxWidth: 560,
    width: '100%',
  },
}));
function Coach() {
  const classes = useStyles();
  const allCoaches = useSelector(coachesSelector);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);

  const { filters: urlFilters, setFilters: setUrlFilters } = useUrlFilters({
    defaultFilters: { search: '' },
  });
  const [searchText, setSearchText] = useState(urlFilters.search || '');

  // Keep URL in sync when search input changes
  useEffect(() => {
    const debouncedUpdate = debounce(() => {
      setUrlFilters({ search: searchText || '' });
    }, DEBOUNCE_DELAY_MS);
    debouncedUpdate();
    return () => debouncedUpdate.cancel();
  }, [searchText]);

  const [formData, setFormData] = useState(null);

  useEffect(() => {
    async function loadCoaches() {
      setLoading(true);
      await dispatch(loadAllCoaches());
      setLoading(false);
    }
    loadCoaches();
    return () => {
      setFormData(null);
    };
  }, []);

  const handleFormSubmit = async (type, data) => {
    setLoading(true);
    if (type === 'create') {
      await dispatch(createCoachAction(data));
    }
    if (type === 'edit') {
      await dispatch(updateCoachAction(data));
    }
    setLoading(false);
    toggleDialog();
  };

  const toggleDialog = () => {
    setOpen((prevState) => !prevState);
    if (open) setFormData(null);
  };

  const editHandler = (row) => {
    setFormData({
      user: row.user,
      userId: row.userId,
      roles: row.roles,
      courseType: row.courseType,
      cohort: row?.cohort,
    });
    toggleDialog();
  };

  const deleteHandler = async (row) => {
    setLoading(true);
    await dispatch(deleteCoachAction(row.userId));
    setLoading(false);
  };


  const addPinHandler = async (row, pin) => {
    setLoading(true);
    await dispatch(updatePinAction({email: row.user.email, pin}));
    setLoading(false);
  };

  const toggleStatusHandler = async (row, pin) => {
    setLoading(true);
    await dispatch(toggleStatusAction({email: row.user.email}));
    setLoading(false);
  };



  const filteredCoaches = useMemo(() => {
    return (
      allCoaches &&
      allCoaches.filter(
        (c) =>
          !searchText.trim() ||
          getFullName(c?.user).toLowerCase().includes(searchText?.toLowerCase())
      )
    );
  }, [searchText, allCoaches]);

  const coachRowData = useMemo(() => {
    return (
      filteredCoaches &&
      filteredCoaches.map((coach) => ({
        ...coach,
        roles: coach?.roles
          ?.map((role) => COACH_ROLES[role])
          .filter((r) => !!r),
      }))
    );
  }, [filteredCoaches]);

  return (
    <div>
      <PageHeader
        title="Coaches"
        subtitle="Manage coach roles and access"
        actions={
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '7px 14px',
                border: '1px solid var(--input-border, #E5E7EB)',
                borderRadius: 'var(--radius-input, 4px)',
                backgroundColor: 'var(--input-background, #fff)',
                transition: 'border-color var(--transition-fast, 150ms)',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'var(--primary-main, #4F46E5)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'var(--input-border, #E5E7EB)';
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary, #6B7280)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type='text'
                placeholder='Search coaches...'
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  fontSize: 13,
                  color: 'var(--text-primary, #111827)',
                  fontFamily: 'inherit',
                  width: 160,
                }}
              />
            </div>
            <button
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--primary-contrast, #fff)',
                backgroundColor: 'var(--primary-main, #4F46E5)',
                border: 'none',
                borderRadius: 'var(--radius-button, 4px)',
                padding: '8px 18px',
                cursor: 'pointer',
                boxShadow: 'var(--shadow-primary-button, 0 2px 8px rgba(79,70,229,0.25))',
                transition: 'background-color var(--transition-fast, 150ms)',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--primary-hover, #4338CA)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--primary-main, #4F46E5)';
              }}
              onClick={toggleDialog}
            >
              Create New Coach
            </button>
          </div>
        }
      />
      {!coachRowData || !coachRowData?.length ? (
        <div className='m-auto w-full mt-20 text-center'>
          <h1>No Data</h1>
        </div>
      ) : (
        <>
          <CoachTable
            rowData={coachRowData}
            editHandler={editHandler}
            deleteHandler={deleteHandler}
            addPinHandler={addPinHandler}
            toggleStatusHandler={toggleStatusHandler}
          />
        </>
      )}
      {open && (
        <CoachFormDialog
          coachRoles={COACH_ROLES}
          open={open}
          handleDialog={toggleDialog}
          formData={formData}
          handleFormSubmit={handleFormSubmit}
          allCoaches={allCoaches}
        />
      )}
      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    </div>
  );
}

export default Coach;

const CoachFormDialog = ({
  coachRoles,
  open,
  handleDialog,
  formData,
  handleFormSubmit,
  allCoaches,
}) => {
  const [user, setUser] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [searchOptions, setSearchOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState({});
  const [courseType, setCourseType] = useState('');
  const [cohort, setCohort] = useState(null);
  const [cohortOptions, setCohortOptions] = useState(null);
  const classes = useStyles();

  async function getCohortOptions() {
    try {
      const res = await getCohortInfoForCoach();
      setCohortOptions(res);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    getCohortOptions();
  }, []);

  useEffect(() => {
    if (formData) {
      setUser(formData.user);
      setCourseType(formData.courseType);
      setRoles(
        Object.keys(coachRoles).reduce((prev, current) => {
          if (formData.roles.includes(coachRoles[current])) {
            return { ...prev, [current]: true };
          }
          return { ...prev, [current]: false };
        }, {})
      );
      setCohort(formData?.cohort?.id ?? null);
    } else {
      setUser(null);
      setCourseType(null);
      setRoles(null);
      setCohort(null);
    }
  }, [formData]);

  const changeHandler = async (value) => {
    setSearchOptions(null);
    const queryResult = await getAllUsers({
      query: value,
      limit: 50,
    });
    if (queryResult.users.length !== 0) {
      setSearchOptions(queryResult.users);
    } else setSearchOptions(null);
    setLoading(false);
  };

  const debounceSearch = useCallback(
    debounce(changeHandler, DEBOUNCE_DELAY_MS),
    [allCoaches]
  );

  useEffect(() => {
    if (inputValue === '') {
      setLoading(false);
      setSearchOptions(null);
    } else if (!user) {
      setLoading(true);
      debounceSearch(inputValue);
    }
  }, [inputValue]);

  const handleChange = (event) => {
    setRoles({ ...roles, [event.target.name]: event.target.checked });
  };

  const handleSubmit = (e, type) => {
    e.preventDefault();
    handleFormSubmit(type, {
      userId: user.id,
      roles: Object.keys(roles).filter((key) => roles[key]),
      courseType,
      cohortId: cohort,
    });
  };

  const selectedRolesCount = roles ? Object.values(roles).filter(Boolean).length : 0;

  return (
    <Dialog
      onClose={handleDialog}
      aria-labelledby='coach-role-dialog'
      open={open}
      classes={{ paper: classes.dialogPaper }}
    >
      <form
        onSubmit={(e) => handleSubmit(e, formData ? 'edit' : 'create')}
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <div
          style={{
            padding: '24px 28px 0',
          }}
        >
          <h2
            id='coach-role-dialog'
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: 'var(--heading-primary, #111827)',
              margin: 0,
            }}
          >
            {formData ? 'Edit Coach Role' : 'Create New Coach Role'}
          </h2>
          <p
            style={{
              fontSize: 13,
              color: 'var(--text-secondary, #6B7280)',
              marginTop: 6,
              marginBottom: 0,
            }}
          >
            {formData
              ? 'Update the coach role assignments below.'
              : 'Assign roles and configure access for a coach.'}
          </p>
        </div>

        {/* Form fields */}
        <div
          style={{
            padding: '20px 28px',
            display: 'flex',
            flexDirection: 'column',
            gap: 18,
            overflowY: 'auto',
            maxHeight: '65vh',
          }}
        >
          {/* User search */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text-secondary, #6B7280)',
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              User {formData ? '(locked)' : ''}
            </label>
            <Autocomplete
              disabled={!!formData}
              options={searchOptions || []}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return `${getFullName(option)} (${option.email})`;
              }}
              value={user}
              onChange={(e, newValue) => setUser(newValue || null)}
              inputValue={inputValue}
              onInputChange={(e, newValue) => setInputValue(newValue)}
              filterOptions={(options) =>
                options.filter(
                  (x) => !allCoaches?.some((coach) => coach?.userId === x?.id)
                )
              }
              loading={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  variant='outlined'
                  placeholder='Search by name or email'
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading && <CircularProgress color='inherit' size={18} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </div>

          {/* User ID (read-only) */}
          {user && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 14px',
                borderRadius: 'var(--radius-input, 4px)',
                backgroundColor: 'var(--active-background, #EEF2FF)',
                border: '1px solid var(--border-light, rgba(0,0,0,0.05))',
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary, #6B7280)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                User ID
              </span>
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--active-text, #4F46E5)', fontFamily: 'monospace' }}>
                {user.id}
              </span>
            </div>
          )}

          {/* Course Type + Cohort row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--text-secondary, #6B7280)',
                  marginBottom: 6,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                Course Type *
              </label>
              <TextField
                select
                name='courseType'
                variant='outlined'
                required
                fullWidth
                value={courseType || ''}
                onChange={(e) => {
                  setCourseType(e.target.value);
                  setCohort(null);
                }}
                placeholder='Select course'
              >
                <MenuItem value={COURSE_TYPE.DATA_SCIENCE}>
                  {COURSE_TYPE_NAMES_MAPPING[COURSE_TYPE.DATA_SCIENCE]}
                </MenuItem>
                <MenuItem value={COURSE_TYPE.WEB3}>
                  {COURSE_TYPE_NAMES_MAPPING[COURSE_TYPE.WEB3]}
                </MenuItem>
                <MenuItem value={COURSE_TYPE.UG_DIPLOMA_CS}>
                  {COURSE_TYPE_NAMES_MAPPING[COURSE_TYPE.UG_DIPLOMA_CS]}
                </MenuItem>
              </TextField>
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--text-secondary, #6B7280)',
                  marginBottom: 6,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                Cohort
              </label>
              <TextField
                select
                name='cohort'
                variant='outlined'
                fullWidth
                value={cohort || ''}
                onChange={(e) => setCohort(e.target.value)}
                placeholder='Select cohort'
                disabled={!courseType}
              >
                {cohortOptions
                  ?.filter((item) => item.courseType === courseType)
                  ?.map((item, id) => (
                    <MenuItem value={item.id ?? cohort} key={id}>
                      {item.name}
                    </MenuItem>
                  ))}
              </TextField>
            </div>
          </div>

          {/* Roles */}
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 10,
              }}
            >
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--text-secondary, #6B7280)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                Assign Roles
              </label>
              {selectedRolesCount > 0 && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--active-text, #4F46E5)',
                    backgroundColor: 'var(--active-background, #EEF2FF)',
                    padding: '2px 8px',
                    borderRadius: 'var(--radius-status-pill, 9999px)',
                  }}
                >
                  {selectedRolesCount} selected
                </span>
              )}
            </div>
            <div
              style={{
                border: '1px solid var(--border-main, rgba(0,0,0,0.07))',
                borderRadius: 'var(--radius-menu, 8px)',
                padding: '8px 12px',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '2px 8px',
                backgroundColor: 'var(--card-background, #fff)',
              }}
            >
              <FormControl component='fieldset' style={{ width: '100%' }}>
                <FormGroup>
                  {Object.keys(coachRoles).map((role) => (
                    <FormControlLabel
                      key={role}
                      control={
                        <Checkbox
                          color='primary'
                          size='small'
                          checked={!!roles?.[role]}
                          onChange={handleChange}
                          name={role}
                          style={{ padding: '4px 8px 4px 4px' }}
                        />
                      }
                      label={
                        <span style={{ fontSize: 13, color: 'var(--text-primary, #111827)' }}>
                          {coachRoles[role]}
                        </span>
                      }
                      style={{ marginLeft: 0, marginRight: 0 }}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </div>
          </div>
        </div>

        {/* Action bar */}
        <div
          style={{
            padding: '16px 28px',
            borderTop: '1px solid var(--border-light, rgba(0,0,0,0.07))',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
          }}
        >
          <button
            type='button'
            onClick={handleDialog}
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--text-secondary, #6B7280)',
              backgroundColor: 'transparent',
              border: '1px solid var(--border-main, rgba(0,0,0,0.07))',
              borderRadius: 'var(--radius-button, 4px)',
              padding: '8px 20px',
              cursor: 'pointer',
              transition: 'all var(--transition-fast, 150ms)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--card-hover, #F9FAFB)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Cancel
          </button>
          <button
            type='submit'
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--primary-contrast, #fff)',
              backgroundColor: 'var(--primary-main, #4F46E5)',
              border: 'none',
              borderRadius: 'var(--radius-button, 4px)',
              padding: '8px 24px',
              cursor: 'pointer',
              boxShadow: 'var(--shadow-primary-button, 0 2px 8px rgba(79,70,229,0.25))',
              transition: 'background-color var(--transition-fast, 150ms)',
              textTransform: 'capitalize',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--primary-hover, #4338CA)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--primary-main, #4F46E5)';
            }}
          >
            {formData ? 'Update Role' : 'Create Role'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};
